<template>
  <div class="flex flex-col">
    <div class="relative flex items-center justify-center">
      <Input
        class="w-full -my-0"
        :type="showPassword ? 'text' : 'password'"
        :placeholder="placeholder"
        :autofocus="autofocus"
        :model-value="modelValue"
        :error="error"
        :autocomplete="autocomplete"
        :aria-label="$attrs['aria-label']"
        :aria-describedby="validatePassword ? 'password-requirements password-status' : undefined"
        :aria-invalid="validatePassword && !isValidPassword"
        @update:modelValue="(value) => $emit('update:modelValue', value)"
        @focus="onInputFocus"
        @blur="onInputBlur"
      />

      <EyeIcon
        :eye-type="showPassword ? 'on' : 'off'"
        class="absolute top-0 right-2
          transition-all
          h-full
          flex items-center justify-center"
        :class="{
          'text-gray-400': !focused && !error,
          'text-red-600': error
        }"
        :style="{
          color: focused && !error ? global.primaryColor : undefined
        }"
        @click="onTogglePassword"
        @mousedown="(e) => e.preventDefault()"
        @mouseup="(e) => e.preventDefault()"
      />
    </div>

    <!-- Status message for screen readers - always present but hidden -->
    <div
      v-if="validatePassword"
      id="password-status"
      class="sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ focusAnnouncementMessage }}
    </div>

    <div
      v-if="validatePassword && showPasswordPolicy && !isValidPassword"
      id="password-requirements"
      class="flex flex-col items-start text-left bg-gray-100 -mt-2 p-2 space-y-2"
      role="region"
      :aria-label="$t('message.accessibility.password_requirements')"
    >
      <p class="text-base text-gray-400">
        {{ $t("message.password_policy_title") }}
      </p>

      <ul>
        <li
          v-for="(item, index) in passwordRequirementsList()"
          :key="index"
          class="flex items-center space-x-1 text-gray-600"
        >
          <CheckmarkIcon :checked="item.validator" />
          <p>{{ item.text }}</p>
        </li>
      </ul>
      <div class="w-full bg-gray-300 h-1 rounded-lg">
        <div
          class="bg-green-600 h-1 rounded-lg"
          :class="statusBarPercentage === 0 ? 'w-0' : `w-${statusBarPercentage}/6`"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Input from './Input.vue';
import CheckmarkIcon from './CheckmarkIcon.vue';
import EyeIcon from './EyeIcon.vue';
import { mapGetters } from 'vuex';

export default defineComponent({
  name: 'PasswordInput',
  components: {
    Input,
    CheckmarkIcon,
    EyeIcon
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    autocomplete: {
      type: String
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    validatePassword: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'update:isValid', 'update:requirements'],
  data () {
    return {
      showPasswordPolicy: false,
      showPassword: false,
      focused: false,
      focusAnnouncementMessage: ''
    };
  },
  watch: {
    isValidPassword (isValid: boolean): void {
      if (this.validatePassword) {
        this.$emit('update:isValid', isValid);
      }
    },
    screenReaderMessage (newMessage: string): void {
      // Update the message only if the field is focused
      if (this.focused && this.validatePassword) {
        this.focusAnnouncementMessage = newMessage;
      }
      // Emit requirements for parent components
      if (this.validatePassword) {
        this.$emit('update:requirements', newMessage);
      }
    }
  },
  mounted() {
    // Emit initial requirements state
    if (this.validatePassword) {
      this.$emit('update:requirements', this.screenReaderMessage);
    }
  },
  methods: {
    onTogglePassword () {
      this.showPassword = !this.showPassword;
    },
    onInputFocus () {
      this.focused = true;
      this.showPasswordPolicy = true;
      // Announce the requirements immediately to the focus group.
      if (this.validatePassword) {
        this.focusAnnouncementMessage = this.screenReaderMessage;
      }
    },
    onInputBlur () {
      this.focused = false;
      if (this.isValidPassword) {
        this.showPasswordPolicy = false;
      }
      // Clean message when user exit
      this.focusAnnouncementMessage = '';
    },
    passwordRequirementsList () {
      return [
        {
          text: this.$t('message.password_policy_spaces'),
          validator: this.passwordMustNotStartOrEndWithSpaces
        },
        {
          text: this.$t('message.password_policy_length'),
          validator: this.passwordHasValidLenght
        },
        {
          text: this.$t('message.password_policy_lowercase'),
          validator: this.passwordHasLowercase
        },
        {
          text: this.$t('message.password_policy_uppercase'),
          validator: this.passwordHasUppercase
        },
        {
          text: this.$t('message.password_policy_number'),
          validator: this.passwordHasNumber
        },
        {
          text: this.$t('message.password_policy_special'),
          validator: this.passwordHasSpecialChar
        }
      ];
    }

  },
  computed: {
    ...mapGetters([
      'passwordPolicy',
      'global'
    ]),
    isValidPassword (): boolean {
      return this.passwordMustNotStartOrEndWithSpaces &&
        this.passwordHasValidLenght &&
        this.passwordHasLowercase &&
        this.passwordHasUppercase &&
        this.passwordHasNumber &&
        this.passwordHasSpecialChar;
    },
    screenReaderMessage (): string {
      const missing = this.passwordRequirementsList()
        .filter((req: any) => !req.validator)
        .map((req: any) => req.text);

      if (missing.length === 0) {
        return 'Password valida';
      }

      return `Requisiti mancanti: ${missing.join(', ')}`;
    },
    statusBarPercentage (): number {
      let percentage = 0;
      if (this.passwordMustNotStartOrEndWithSpaces) {
        percentage++;
      }
      if (this.passwordHasValidLenght) {
        percentage++;
      }
      if (this.passwordHasLowercase) {
        percentage++;
      }
      if (this.passwordHasUppercase) {
        percentage++;
      }
      if (this.passwordHasNumber) {
        percentage++;
      }
      if (this.passwordHasSpecialChar) {
        percentage++;
      }
      return percentage;
    },
    passwordMustNotStartOrEndWithSpaces (): boolean {
      return this.passwordPolicy.noStartingSpacesRgx.test(this.modelValue) &&
        this.passwordPolicy.noEndingSpacesRgx.test(this.modelValue);
    },
    passwordHasValidLenght (): boolean {
      return this.modelValue?.length >= this.passwordPolicy.length;
    },
    passwordHasLowercase (): boolean {
      return this.passwordPolicy.lowerCaseRgx.test(this.modelValue);
    },
    passwordHasUppercase (): boolean {
      return this.passwordPolicy.upperCaseRgx.test(this.modelValue);
    },
    passwordHasNumber (): boolean {
      return this.passwordPolicy.numberRgx.test(this.modelValue);
    },
    passwordHasSpecialChar (): boolean {
      return this.passwordPolicy.specialCharRgx.test(this.modelValue);
    }
  }
});
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
